// src/components/UI/TutorialTooltip.js
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './TutorialTooltip.module.css';
import { useTutorial } from '../../context/TutorialContext';

const SPACING = 24;
const VERTICAL_OFFSET = 16;
const SPOTLIGHT_PAD = 6;
const TOOLTIP_MAX_WIDTH = 320;

const CursorIcon = () => (
  <svg
    className={styles.cursorSvg}
    width="36"
    height="41"
    viewBox="0 0 36 41"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M32.2276 22.3647C34.0187 22.0851 34.4731 19.715 32.9124 18.7928L4.85066 2.21084C3.41788 1.3642 1.65644 2.6046 1.97073 4.23889L8.34964 37.4092C8.7036 39.2498 11.2078 39.5587 11.9984 37.8592L17.7517 25.4918C18.0235 24.9076 18.5687 24.4973 19.2053 24.3979L32.2276 22.3647Z"
      fill="white"
      stroke="#ACCCEF"
      strokeWidth="3.86377"
      strokeLinecap="square"
    />
  </svg>
);

// Reusable tutorial overlay: dims the screen, spotlights `targetRef`,
// and shows a content-sized tooltip next to it.
const TutorialTooltip = ({
  show,
  targetRef,
  title,
  description,
  currentStep = 1,
  totalSteps = 1,
  placement = 'right',
  showClickHere = true,
  showCursor = true,
  showDots = true,
  cursorAnchor = 'auto',
  scrollIntoView = false,
  extraTargetRefs,
  noDim = false,
  onSkip,
  onNext,
  nextLabel,
  skipLabel = 'Skip',
  clickHereLabel = 'Click here',
  children,
}) => {
  // Default to dismissing the tutorial when the caller hasn't supplied an
  // explicit onSkip — so every blue box gets a Skip button by default.
  const tutorial = useTutorial();
  const effectiveOnSkip = onSkip === undefined && tutorial.active ? tutorial.dismiss : onSkip;

  const [rect, setRect] = useState(null);
  const [extraRects, setExtraRects] = useState([]);
  const [actionRect, setActionRect] = useState(null);
  const [tooltipRect, setTooltipRect] = useState(null);
  const [tooltipNode, setTooltipNodeState] = useState(null);
  const [ready, setReady] = useState(false);
  const [trackedTarget, setTrackedTarget] = useState(targetRef);
  const actionButtonRef = useRef(null);
  const tooltipRef = useRef(null);
  const maskId = useId();

  // Mirror extraTargetRefs into a ref so the `update` closure inside
  // useLayoutEffect always sees the latest array without re-running the effect.
  const extraTargetRefsRef = useRef(extraTargetRefs);
  extraTargetRefsRef.current = extraTargetRefs;
  const updateRef = useRef(() => {});

  // Callback ref so we can measure the tooltip the moment it lands in the DOM
  // (before paint), avoiding a frame where cursorAnchor='tooltip' falls back
  // to the spotlight rect.
  const setTooltipNode = useCallback((node) => {
    tooltipRef.current = node;
    setTooltipNodeState(node);
    if (node) {
      const r = node.getBoundingClientRect();
      setTooltipRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }
  }, []);

  // Tooltip size can change after mount (e.g., async-loaded images in children)
  // — observe it so cursor/click-here positions stay anchored to the bottom.
  useEffect(() => {
    if (!tooltipNode || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      const r = tooltipNode.getBoundingClientRect();
      setTooltipRect((prev) => {
        if (
          prev &&
          prev.top === r.top &&
          prev.left === r.left &&
          prev.width === r.width &&
          prev.height === r.height
        ) {
          return prev;
        }
        return { top: r.top, left: r.left, width: r.width, height: r.height };
      });
    });
    observer.observe(tooltipNode);
    return () => observer.disconnect();
  }, [tooltipNode]);
  const [viewport, setViewport] = useState({
    w: typeof window !== 'undefined' ? window.innerWidth : 0,
    h: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  // When the spotlight target changes (step transition), drop positional state
  // synchronously during render so we never paint the previous step's coords.
  if (trackedTarget !== targetRef) {
    setTrackedTarget(targetRef);
    setRect(null);
    setExtraRects([]);
    setActionRect(null);
    setTooltipRect(null);
    setReady(false);
  }

  useLayoutEffect(() => {
    if (!show || !targetRef?.current) {
      setRect(null);
      setActionRect(null);
      setReady(false);
      return undefined;
    }
    let cancelled = false;
    setReady(false);

    let lastRectKey = '';
    let lastActionKey = '';
    let lastExtrasKey = '';
    const update = () => {
      const el = targetRef.current;
      if (el && el.isConnected !== false) {
        const r = el.getBoundingClientRect();
        // Ignore degenerate rects from detached or not-yet-laid-out nodes —
        // otherwise the tooltip would briefly snap to the viewport's top-left.
        if (r.width > 0 || r.height > 0) {
          const next = { top: r.top, left: r.left, width: r.width, height: r.height };
          const key = `${next.top},${next.left},${next.width},${next.height}`;
          if (key !== lastRectKey) {
            lastRectKey = key;
            setRect(next);
          }
        }
      }
      const currentExtras = extraTargetRefsRef.current;
      if (Array.isArray(currentExtras) && currentExtras.length) {
        const next = currentExtras
          .map((r) => r?.current)
          .filter(Boolean)
          .map((node) => {
            const b = node.getBoundingClientRect();
            return { top: b.top, left: b.left, width: b.width, height: b.height };
          });
        const key = next.map((n) => `${n.top},${n.left},${n.width},${n.height}`).join('|');
        if (key !== lastExtrasKey) {
          lastExtrasKey = key;
          setExtraRects(next);
        }
      } else if (lastExtrasKey !== '') {
        lastExtrasKey = '';
        setExtraRects([]);
      }
      if (actionButtonRef.current) {
        const r = actionButtonRef.current.getBoundingClientRect();
        const next = { top: r.top, left: r.left, width: r.width, height: r.height };
        const key = `${next.top},${next.left},${next.width},${next.height}`;
        if (key !== lastActionKey) {
          lastActionKey = key;
          setActionRect(next);
        }
      } else if (lastActionKey !== '') {
        lastActionKey = '';
        setActionRect(null);
      }
      if (tooltipRef.current) {
        const r = tooltipRef.current.getBoundingClientRect();
        setTooltipRect((prev) => {
          if (
            prev &&
            prev.top === r.top &&
            prev.left === r.left &&
            prev.width === r.width &&
            prev.height === r.height
          ) {
            return prev;
          }
          return { top: r.top, left: r.left, width: r.width, height: r.height };
        });
      }
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    };

    // Wait for any CSS animation on the target or its ancestors to finish,
    // then read the final rect and reveal the overlay with a fade-in.
    // If `scrollIntoView` is set, also smooth-scroll the target into view first
    // and wait for the scroll to settle before revealing the tooltip.
    const waitForAnimations = async () => {
      let cur = targetRef.current;
      const animations = [];
      while (cur && cur !== document.body) {
        if (typeof cur.getAnimations === 'function') {
          animations.push(...cur.getAnimations({ subtree: false }));
        }
        cur = cur.parentElement;
      }
      if (animations.length) {
        await Promise.all(
          animations.map((a) => (a.finished ? a.finished.catch(() => {}) : Promise.resolve()))
        );
      }
      if (scrollIntoView && targetRef.current) {
        targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    };

    updateRef.current = update;

    waitForAnimations().then(() => {
      if (cancelled) return;
      update();
      // Poll for up to ~1.5s after mount so we keep up with layout that
      // settles asynchronously (lazy images, fonts, parent reflows, etc.).
      // Stop early once the rect has been stable for ~150ms.
      const start = performance.now();
      let stableSince = null;
      let lastKey = '';
      const tick = () => {
        if (cancelled) return;
        update();
        const el = targetRef.current;
        const r = el ? el.getBoundingClientRect() : null;
        const key = r ? `${r.top},${r.left},${r.width},${r.height}` : '';
        if (key !== lastKey) {
          lastKey = key;
          stableSince = performance.now();
        }
        const stableFor = stableSince ? performance.now() - stableSince : 0;
        const elapsed = performance.now() - start;
        if (!cancelled && elapsed < 1500 && stableFor < 150) {
          requestAnimationFrame(tick);
        } else {
          setReady(true);
        }
      };
      requestAnimationFrame(tick);
    });

    window.addEventListener('resize', update);
    // Intentionally NOT listening for scroll: the tooltip/spotlight should
    // stay frozen at its initial viewport position so users can scroll the
    // page underneath without dragging the overlay along.
    return () => {
      cancelled = true;
      window.removeEventListener('resize', update);
    };
  }, [show, targetRef, onNext, nextLabel, title, description, scrollIntoView]);

  // Re-measure when the set of extra spotlight targets changes after mount
  // (e.g., a new spotlight rect appears as the user interacts).
  useEffect(() => {
    if (updateRef.current) updateRef.current();
  }, [extraTargetRefs?.length]);

  // Keep ResizeObservers running for the lifetime of the overlay so any later
  // layout shift (async images / fonts pushing the target around, parent
  // reflows) gets caught even after the initial ~1.5s polling window ends.
  useEffect(() => {
    if (!show || typeof ResizeObserver === 'undefined') return undefined;
    const observers = [];
    const trigger = () => updateRef.current && updateRef.current();
    const observe = (el) => {
      if (!el) return;
      const obs = new ResizeObserver(trigger);
      obs.observe(el);
      observers.push(obs);
    };
    observe(targetRef?.current);
    observe(document.body);
    if (Array.isArray(extraTargetRefsRef.current)) {
      extraTargetRefsRef.current.forEach((r) => observe(r?.current));
    }
    return () => observers.forEach((o) => o.disconnect());
  }, [show, targetRef, extraTargetRefs?.length]);

  // We deliberately don't lock page scroll anymore — the spotlight just
  // freezes at its initial viewport coords (see the scroll listener below)
  // so the user can scroll the underlying page without the tooltip drifting.

  if (!show || !rect) return null;

  // Auto-flip placement if not enough room on the requested side
  const resolvedPlacement = (() => {
    if (placement === 'right' && rect.left + rect.width + SPACING + TOOLTIP_MAX_WIDTH > viewport.w) {
      return 'left';
    }
    if (placement === 'left' && rect.left - SPACING - TOOLTIP_MAX_WIDTH < 0) {
      return 'right';
    }
    return placement;
  })();

  const tooltipStyle = (() => {
    if (resolvedPlacement === 'right') {
      return { top: rect.top + VERTICAL_OFFSET, left: rect.left + rect.width + SPACING };
    }
    if (resolvedPlacement === 'left') {
      return { top: rect.top + VERTICAL_OFFSET, right: viewport.w - rect.left + SPACING };
    }
    if (resolvedPlacement === 'bottom') {
      return { top: rect.top + rect.height + SPACING, left: rect.left };
    }
    if (resolvedPlacement === 'top-end') {
      // Tooltip sits above-and-to-the-left of the target.
      // Arrow is 24px from tooltip's right (see .arrowBottomEnd in CSS),
      // so anchor the tooltip such that the arrow tip lands on the target's center.
      return {
        bottom: viewport.h - rect.top + SPACING,
        right: viewport.w - (rect.left + rect.width / 2) - 24,
      };
    }
    if (resolvedPlacement === 'top-start') {
      // Mirror of 'top-end': tooltip sits above-and-to-the-right of the target.
      // Arrow is 24px from tooltip's left, lands at the target's center.
      return {
        bottom: viewport.h - rect.top + SPACING,
        left: rect.left + rect.width / 2 - 24,
      };
    }
    if (resolvedPlacement === 'top-center') {
      // Tooltip horizontally centered above target, arrow at bottom-center.
      return {
        bottom: viewport.h - rect.top + SPACING,
        left: rect.left + rect.width / 2,
        transform: 'translateX(-50%)',
      };
    }
    // 'top'
    return { bottom: viewport.h - rect.top + SPACING, left: rect.left };
  })();

  const allSpotlightRects = [rect, ...extraRects];

  // Build an evenodd path: full viewport with one rounded-rect hole per
  // spotlight. Using <clipPath> (not <mask>) so the cutout areas are
  // genuinely outside the painted shape, which makes them ignore pointer
  // events while the dim area still swallows clicks.
  const buildDimPath = () => {
    const outer = `M0,0 H${viewport.w} V${viewport.h} H0 Z`;
    const holes = allSpotlightRects
      .map((r) => {
        const x = r.left - SPOTLIGHT_PAD;
        const y = r.top - SPOTLIGHT_PAD;
        const w = r.width + SPOTLIGHT_PAD * 2;
        const h = r.height + SPOTLIGHT_PAD * 2;
        const rr = Math.min(12, w / 2, h / 2);
        if (w <= 0 || h <= 0) return '';
        return (
          `M${x + rr},${y} ` +
          `h${w - 2 * rr} ` +
          `a${rr},${rr} 0 0 1 ${rr},${rr} ` +
          `v${h - 2 * rr} ` +
          `a${rr},${rr} 0 0 1 ${-rr},${rr} ` +
          `h${-(w - 2 * rr)} ` +
          `a${rr},${rr} 0 0 1 ${-rr},${-rr} ` +
          `v${-(h - 2 * rr)} ` +
          `a${rr},${rr} 0 0 1 ${rr},${-rr} ` +
          'Z'
        );
      })
      .join(' ');
    return `${outer} ${holes}`;
  };

  // Resolve which rect the cursor + click-here pill should anchor to.
  // Priority: explicit `tooltip` anchor > explicit `target` anchor > action button (if rendered) > spotlight target.
  const anchorRect =
    cursorAnchor === 'tooltip' && tooltipRect
      ? { ...tooltipRect, fromTooltip: true }
      : cursorAnchor === 'target'
        ? { ...rect, fromTarget: true }
        : actionRect
          ? { ...actionRect, fromAction: true }
          : { ...rect, fromTarget: true };

  const cursorPos = anchorRect.fromTooltip
    ? {
        top: anchorRect.top + anchorRect.height - 6,
        left: anchorRect.left + anchorRect.width / 2 + 60,
      }
    : anchorRect.fromAction
      ? {
          top: anchorRect.top + anchorRect.height + 6,
          left: anchorRect.left + anchorRect.width * 0.7,
        }
      : resolvedPlacement === 'bottom'
        ? {
            // Tooltip is below the spotlight, so place the cursor to the
            // RIGHT of the spotlight to avoid overlapping the tooltip box.
            top: anchorRect.top + anchorRect.height / 2,
            left: anchorRect.left + anchorRect.width + 28,
          }
        : {
            top: anchorRect.top + anchorRect.height + 8,
            left: anchorRect.left + anchorRect.width * 0.72,
          };
  const clickHerePos = anchorRect.fromTooltip
    ? {
        top: anchorRect.top + anchorRect.height + 26,
        left: anchorRect.left + anchorRect.width / 2 + 60,
      }
    : anchorRect.fromAction
      ? {
          top: anchorRect.top + anchorRect.height + 36,
          left: anchorRect.left + anchorRect.width * 0.7,
        }
      : resolvedPlacement === 'bottom'
        ? {
            top: anchorRect.top + anchorRect.height / 2 + 36,
            left: anchorRect.left + anchorRect.width + 28,
          }
        : {
            top: anchorRect.top + anchorRect.height + 34,
            left: anchorRect.left + anchorRect.width * 0.72,
          };

  const arrowClass = {
    right: styles.arrowLeft,
    left: styles.arrowRight,
    top: styles.arrowBottom,
    'top-end': styles.arrowBottomEnd,
    'top-start': styles.arrowBottomStart,
    'top-center': styles.arrowBottomCenter,
    bottom: styles.arrowTop,
  }[resolvedPlacement];

  return createPortal(
    <div
      className={`${styles.root} ${ready ? styles.rootReady : ''}`}
      role="presentation"
    >
      {!noDim && (
        <svg
          className={styles.dimSvg}
          width={viewport.w}
          height={viewport.h}
          preserveAspectRatio="none"
        >
          <path
            d={buildDimPath()}
            fillRule="evenodd"
            fill="rgba(50, 64, 79, 0.40)"
          />
        </svg>
      )}

      {showCursor && (cursorAnchor !== 'tooltip' || tooltipRect) && (
        <div className={styles.cursor} style={cursorPos}>
          <CursorIcon />
        </div>
      )}

      {showClickHere && (cursorAnchor !== 'tooltip' || tooltipRect) && (
        <div className={styles.clickHere} style={clickHerePos}>
          <span className={styles.clickHereText}>{clickHereLabel}</span>
        </div>
      )}

      <div
        ref={setTooltipNode}
        className={`${styles.tooltip} ${arrowClass || ''}`}
        style={tooltipStyle}
        role="dialog"
        aria-label={title}
      >
        {title && <h3 className={styles.title}>{title}</h3>}
        {description && <p className={styles.body}>{description}</p>}
        {children}
        {effectiveOnSkip && (
          <button type="button" className={styles.skip} onClick={effectiveOnSkip}>
            {skipLabel}
          </button>
        )}
        <div className={styles.footer}>
          {onNext && (
            <div className={styles.actions}>
              <button
                ref={actionButtonRef}
                type="button"
                className={styles.next}
                onClick={onNext}
              >
                {nextLabel || (currentStep === totalSteps ? 'Done' : 'Next')}
              </button>
            </div>
          )}
          {showDots && totalSteps > 0 && (
            <div className={styles.dots} aria-hidden="true">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <span
                  key={i}
                  className={i + 1 === currentStep ? styles.dotActive : styles.dot}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TutorialTooltip;
